import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import cx from 'classnames';

import CloseButton from '../CloseButton';
import useDelayedUnmount from '../../../hooks/useDelayedUnmount';

import './BottomSheet.scss';

// How far down, as a share of the panel's height, or how fast, a drag on the header has to go to
// close the sheet rather than spring back
const CLOSE_SHARE = 0.25;
const CLOSE_SPEED = 0.5; // px per ms

/**
 * A panel that slides up from the bottom over whatever dialog opened it, and is dragged down by its
 * header to close. The shell only: the caller fills the body and, if it wants them, the footer and
 * buttons in the header's corners, and a HeaderSheet to drop from under the header as in Modal.
 *
 * `className` lands on the root, so the caller's own stylesheet nests its content under it.
 */
function BottomSheet({ isOpen, onClose, title, className, action, endAction, sheet, footer, children }) {
  const mounted = useDelayedUnmount(isOpen);
  const panelRef = useRef(null);
  const backdropRef = useRef(null);
  const drag = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    // Reopened while still leaving: drop whatever a drag left on the panel
    if (panelRef.current) panelRef.current.style.transform = '';
    if (backdropRef.current) backdropRef.current.style.opacity = '';
  }, [isOpen]);

  // Capture phase, so Escape closes the sheet before the dialog under it sees it
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event) => {
      // A HeaderSheet down inside it takes Escape first
      if (event.key !== 'Escape' || panelRef.current?.querySelector('.HeaderSheet:not(.is-closing)')) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      onClose();
    };

    window.addEventListener('keydown', handleEscape, true);

    return () => window.removeEventListener('keydown', handleEscape, true);
  }, [isOpen, onClose]);

  if (!mounted) return null;

  /**
   * Dragging the header down, like a phone's sheets. Only the header takes the drag, so the body
   * still scrolls. Moved in the panel's own units: under the builder's zoom a pixel of pointer is
   * less than a pixel of panel.
   */
  const startDrag = (event) => {
    if (event.button !== 0 || event.target.closest('button, .HeaderSheet')) return;

    const panel = panelRef.current;

    event.currentTarget.setPointerCapture(event.pointerId);
    panel.style.transition = 'none';
    drag.current = {
      startY: event.clientY,
      startTime: event.timeStamp,
      dy: 0,
      height: panel.getBoundingClientRect().height,
      scale: panel.getBoundingClientRect().height / panel.offsetHeight || 1,
    };
  };

  const moveDrag = (event) => {
    const state = drag.current;

    if (!state) return;

    state.dy = Math.max(0, event.clientY - state.startY);
    panelRef.current.style.transform = `translateY(${state.dy / state.scale}px)`;
    backdropRef.current.style.opacity = String(1 - state.dy / state.height);
  };

  const endDrag = (event) => {
    const state = drag.current;

    if (!state) return;

    drag.current = null;

    const panel = panelRef.current;
    const speed = state.dy / Math.max(1, event.timeStamp - state.startTime);

    panel.style.transition = '';

    // Past the threshold it carries on off the bottom while the dismiss fades it
    if (state.dy > state.height * CLOSE_SHARE || (speed > CLOSE_SPEED && state.dy > 20)) {
      panel.style.transform = 'translateY(100%)';
      onClose();

      return;
    }

    panel.style.transform = '';
    backdropRef.current.style.opacity = '';
  };

  return createPortal(
    <div className={cx('BottomSheet', className, { 'is-closing': !isOpen })}>
      <div ref={backdropRef} className="backdrop" onClick={onClose} aria-hidden="true" />

      <div ref={panelRef} className="panel" role="dialog" aria-modal="true" aria-label={title}>
        <div
          className="header"
          onPointerDown={startDrag}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <span className="grabber" aria-hidden="true" />
          <div className="action">{action}</div>
          <h3 className="title">{title}</h3>
          <div className="end">
            {endAction}
            <CloseButton onClick={onClose} className="close" />
          </div>
          {sheet}
        </div>

        <div className="body">{children}</div>

        {footer && <div className="footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

BottomSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** The heading, and the dialog's accessible name */
  title: PropTypes.string.isRequired,
  className: PropTypes.string,
  /** A button for the header's left corner, opposite the X */
  action: PropTypes.node,
  /** A button for the header's right corner, beside the X */
  endAction: PropTypes.node,
  /** A HeaderSheet, hung off the header and covering the body */
  sheet: PropTypes.node,
  footer: PropTypes.node,
  children: PropTypes.node,
};

export default BottomSheet;
